import React, {Fragment} from 'react'
import {compact, isEmpty} from 'lodash'
import {formatDate, formatNumber, formatTime, frequencyInWords, titlecase} from '../../lib/format'
import {useParams, Link} from 'react-router-dom'
import ModalLink from '../shared/ModalLink'

const LinkIf = ({to, children, ...props}) => {
  if (!to) return children
  return (
    <a href={to} {...props}>
      {children}
    </a>
  )
}

const translateBool = (old_value, new_value, on_message, off_message) => {
  const oldBool = !!old_value
  const newBool = !!new_value
  if (oldBool === newBool) return null // no change
  return !oldBool && newBool ? on_message : off_message
}

const Change = ({attribute, before, after}) => {
  if (attribute.endsWith('_at')) {
    before = formatTime(before)
    after = formatTime(after)
  }

  switch (attribute) {
    case 'deleted_at':
      if (!isEmpty(before) && isEmpty(after)) {
        return 'Restored (legacy)'
      } else if (isEmpty(before) && !isEmpty(after)) {
        return 'Deleted (legacy)'
      } else {
        return null
      }
    case 'suspended_at':
      if (!isEmpty(before) && isEmpty(after)) {
        return 'Unsuspended'
      } else if (isEmpty(before) && !isEmpty(after)) {
        return 'Suspended'
      } else {
        return null
      }
    case 'use_running_balance_invoices':
      return translateBool(before, after, 'Enabled running balance invoices', 'Disabled running balance invoices')
    case 'warn_when_overdue':
      return translateBool(before, after, 'Enabled SMS warnings when overdue', 'Disabled SMS warnings when overdue')
    case 'is_sendable':
      return translateBool(before, after, 'Enabled email sending', 'Disabled email sending')
    case 'reconciled':
      return translateBool(before, after, 'Reconciled', 'Unreconciled')
    case 'is_draft':
      return translateBool(before, after, 'Saved as Draft', 'Approved')
    case 'line_items':
      if (!after || !after.length) return <span>Removed line items</span>

      if (!before || !before.length) {
        return (
          <Fragment>
            Added line items{' '}
            {after.map((item, i) => (
              <Fragment key={i}>
                <b>
                  {item.description} {parseInt(item.quantity, 10)} x{' '}
                  {formatNumber(item.unit_price_excl_gst, {thousands: true, currency: true})}
                </b>
                {i < after.length - 1 ? ', ' : null}
              </Fragment>
            ))}
          </Fragment>
        )
      }

      const allChanges = []
      before.forEach((oldItem, index) => {
        const itemChanges = {}
        const newItem = after[index] || {}
        const allKeys = new Set([...Object.keys(oldItem), ...Object.keys(newItem)])

        allKeys.forEach(key => {
          if (oldItem[key] !== newItem[key]) {
            itemChanges[key] = {from: oldItem[key], to: newItem[key]}
          }
        })

        if (Object.keys(itemChanges).length) allChanges.push(itemChanges)
      })

      return (
        <Fragment>
          {allChanges.map(change =>
            Object.keys(change).map((key, i) => (
              <Fragment key={i}>
                Line item {key} changed from <b>{change[key].from}</b> to <b>{change[key].to}</b>
              </Fragment>
            ))
          )}
        </Fragment>
      )
    case 'status':
      return (
        {
          pending: 'Changed status to pending',
          active: 'Activated',
          inactive: 'De-activated'
        }[after] || null
      )
    case 'state':
      return (
        {
          draft: 'Reverted to draft state',
          active: 'Activated',
          archived: 'Archived',
          reconciled: 'Reconciled',
          approved: 'Approved',
          locked: 'Locked'
        }[after] || null
      )
    default:
      if (!isEmpty(before) && isEmpty(after)) {
        return (
          <Fragment>
            Removed {titlecase(attribute).toLowerCase()} <b>{before}</b>
          </Fragment>
        )
      } else if (isEmpty(before) && !isEmpty(after)) {
        return (
          <Fragment>
            Added {titlecase(attribute).toLowerCase()} <b>{after}</b>
          </Fragment>
        )
      } else if (!isEmpty(after)) {
        return (
          <Fragment>
            Changed {titlecase(attribute).toLowerCase()}{' '}
            {!isEmpty(before) ? (
              <Fragment>
                from <b>{before}</b>{' '}
              </Fragment>
            ) : null}
            to <b>{after}</b>
          </Fragment>
        )
      } else {
        return null
      }
  }
}

const Context = ({model, context, action}) => {
  if (!context) return null
  switch (model) {
    case 'Invoice':
      return context.invoice_number
    case 'Account':
      return !isEmpty(context.name)
        ? context.name
        : compact([context.contact_first_name, context.contact_last_name]).join(' ')
    case 'Statement':
      return (
        <Fragment>
          dated {context.from_date && `from ${formatDate(context.from_date)}`}
          to {formatDate(context.to_date)}
          {context.email_id ? (
            <Fragment>
              ;{' '}
              <LinkIf to={context.email_download_url} target='_blank'>
                Email {context.email_status}
              </LinkIf>{' '}
              to {compact([context.email_to, context.email_cc, context.email_bcc]).join(', ')}
            </Fragment>
          ) : null}
        </Fragment>
      )
    case 'Service':
      return compact([context.address, context.description]).join(' - ')
    case 'RepeatingInvoice':
      return action == 'attached'
        ? `for $${context.amount} every ${frequencyInWords(context.frequency_count, context.frequency)}`
        : null
    case 'Payment':
      return context.amount
    case 'Group':
      return context.name
    case 'TextMessage':
      return {first_warning: 'as First SMS Warning', final_warning: 'as Final SMS Warning'}[context.identifier] || null
    default:
      return null
  }
}

export default ({details}) => {
  const {organisationID} = useParams()

  return details.map((detail, i) => {
    if (detail.action === 'changed') {
      return (
        <div key={i} className='has-text-dark'>
          <Change {...detail} />
        </div>
      )
    } else {
      return (
        <div key={i} className='has-text-dark'>
          {titlecase(detail.action)}{' '}
          <Fragment>
            {detail.model === 'Invoice' ? (
              <Link to={`/orgs/${organisationID}/invoices/${detail.id}`}>
                {detail.model}#{detail.id}
              </Link>
            ) : detail.model === 'RepeatingInvoice' ? (
              <Link to={`/orgs/${organisationID}/repeating_invoices/${detail.id}`}>
                {detail.model}#{detail.id}
              </Link>
            ) : detail.model === 'Group' ? (
              <Link
                to={{
                  pathname: `/orgs/${organisationID}/groups/custom`,
                  search: `modals=group&groupId=${detail.id}&type=custom`
                }}>
                {detail.model}#{detail.id}
              </Link>
            ) : detail.model === 'Service' ? (
              <ModalLink modal='service' params={{serviceId: detail.id}}>
                {detail.model}#{detail.id}
              </ModalLink>
            ) : detail.model === 'TextMessage' ? (
              <ModalLink modal='text-message' params={{textMessageId: detail.id}}>
                {detail.model}#{detail.id}
              </ModalLink>
            ) : detail.model === 'Statement' ? (
              <Fragment>
                {detail.model}#{detail.id}
                {detail.emailDownloadUrl ? (
                  <a href={detail.emailDownloadUrl} target='_blank'>
                    Download
                  </a>
                ) : null}
              </Fragment>
            ) : (
              `${detail.model}#${detail.id}`
            )}{' '}
            <Context {...detail} />
          </Fragment>
        </div>
      )
    }
  })
}
